import React, { useEffect, useRef, useCallback, useState } from 'react';
import { LightbulbIcon, MapPinIcon, ArrowRightIcon, PlusIcon } from '@phosphor-icons/react';

import { Dialog } from '../../../../components/dialog/Dialog';
import { Button } from '../../../../components/button/Button';
import { EmptyState } from '../../../../components/empty-state/EmptyState';
import { Alert } from '../../../../components/alert/Alert';
import { DelayedSpinner } from '../../../../components/Spinner';
import type { PlanningBoardTruck, PlanningBoardBacklogOrder } from '../../models';
import { suggestPlanningBoardLoads } from '../../models';
import type { PlanningBoardSuggestedOrder } from '../../endpoints/orders/PlanningBoardSuggestLoadsEndpoint.types';
import { decimateAndFormatNumber } from '../../../../utils/number';

// ============================================================================
// Types
// ============================================================================

interface SuggestLoadsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  truck: PlanningBoardTruck;
  date: string;
  onSelectOrder: (order: PlanningBoardBacklogOrder) => void;
}

// ============================================================================
// Component
// ============================================================================

export const SuggestLoadsDialog: React.FC<SuggestLoadsDialogProps> = ({
  isOpen,
  onClose,
  truck,
  date,
  onSelectOrder,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<PlanningBoardSuggestedOrder[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Abort controller ref to prevent race conditions when dialog opens/closes rapidly
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchSuggestions = useCallback(async () => {
    // Cancel any in-flight request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    setError(null);

    try {
      const response = await suggestPlanningBoardLoads({
        truckId: truck.id,
        date,
        limit: 20,
      });

      // Check if this request was cancelled while awaiting
      if (controller.signal.aborted) return;

      setSuggestions(response.suggestions);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError('Failed to fetch suggestions');
      console.error(err);
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [truck.id, date]);

  // Fetch suggestions when dialog opens, cleanup on close
  useEffect(() => {
    if (isOpen) {
      fetchSuggestions();
    }

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [isOpen, fetchSuggestions]);

  const hasLocation = truck.currentLocation !== null;

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Suggest Loads">
      <div className="space-y-4">
        {/* Truck info */}
        <div className="flex items-center gap-3 p-3 bg-surface-sunken border border-border rounded-lg">
          <div className="flex-1">
            <div className="font-medium">{truck.licensePlate}</div>
            <div className="text-sm text-fg-muted">
              {truck.driverDisplayName?.trim() || truck.driverName || truck.name || '-'}
            </div>
          </div>
          {hasLocation ? (
            <div className="text-sm text-right">
              <div className="flex items-center gap-1 text-fg-muted">
                <MapPinIcon className="w-4 h-4" />
                Current location
              </div>
              <div className="font-medium">
                {truck.currentLocation?.country} - {truck.currentLocation?.city}
              </div>
            </div>
          ) : (
            <div className="text-sm text-fg-muted">No location set</div>
          )}
        </div>

        {/* No location warning */}
        {!hasLocation && (
          <Alert type="warning" icon={<LightbulbIcon className="w-5 h-5" />}>
            Set the truck's current location to get load suggestions based on proximity.
          </Alert>
        )}

        {/* Loading state */}
        {isLoading && <DelayedSpinner size={8} className="flex items-center justify-center py-8" />}

        {/* Error state */}
        {error && <Alert type="error">{error}</Alert>}

        {/* Suggestions list */}
        {!isLoading && !error && suggestions.length === 0 && (
          <EmptyState
            icon={<LightbulbIcon className="w-10 h-10 text-fg-muted" />}
            title="No suggestions available"
            description="There are no unassigned orders matching this truck."
            className="bg-surface-sunken rounded-lg"
          />
        )}

        {!isLoading && !error && suggestions.length > 0 && (
          <div className="space-y-2 max-h-80 overflow-auto">
            {suggestions.map((suggestion) => {
              const allStops = suggestion.lines.flatMap((l) => l.stops);
              const firstLoad = allStops.find((s) => s.type === 'Load');
              const lastUnload = [...allStops].reverse().find((s) => s.type === 'Unload');

              return (
                <div
                  key={suggestion.id}
                  className="flex items-center gap-3 p-3 border border-border rounded-lg hover:border-border-strong transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{suggestion.orderNumber ?? 'NEW'}</span>
                      {hasLocation && suggestion.distance > 0 && (
                        <span className="text-xs text-fg-muted">{suggestion.distance} km away</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-fg-muted">
                      {firstLoad && (
                        <>
                          <span className="font-medium">{firstLoad.location.country}</span>
                          <span>{firstLoad.location.city}</span>
                        </>
                      )}
                      <ArrowRightIcon className="w-3 h-3 text-fg-muted" />
                      {lastUnload && (
                        <>
                          <span className="font-medium">{lastUnload.location.country}</span>
                          <span>{lastUnload.location.city}</span>
                        </>
                      )}
                    </div>
                    <div className="text-xs text-brand-text mt-1">
                      €{decimateAndFormatNumber(suggestion.saleTotal, 2, { decimalSeperator: ',' })}
                    </div>
                  </div>
                  <Button
                    color="primary"
                    onTrigger={() => {
                      onSelectOrder(suggestion);
                      onClose();
                    }}
                    iconLeft={<PlusIcon className="button-icon" />}
                  >
                    Add
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end pt-2 border-t border-border">
          <Button color="secondary" onTrigger={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
